import ProductHeroContent from '@blocks/product/product-hero-content'
import ProductHeroGallery from '@blocks/product/product-hero-gallery'
import { type SanityShoppingInfo } from '@data/sanity/queries/types/site'
import {
  getFallbackVariantFromOptions,
  getOptionListLabel,
  getVariantFromOptions,
} from '@lib/product'
import { getProductOptions } from '@lib/product-options'
import { ProductCombinedListingContext } from '@lib/product/combined-listing/context'
import { useCallback, useContext, useMemo, useState } from 'react'

interface CombinedListingProductHeroProps {
  shoppingInfo?: SanityShoppingInfo
  enablePreview?: boolean
  className?: string
}

const CombinedListingProductHero = ({
  shoppingInfo,
  enablePreview = false,
  className,
}: CombinedListingProductHeroProps) => {
  const {
    activeCombinedListing,
    activeProduct,
    activeVariant,
    productGalleryPhotos,
    productOptionSettings,
    productVariants,
    productOptions,
    setActiveVariantId,
  } = useContext(ProductCombinedListingContext)

  const [previewVariantId, setPreviewVariantId] = useState<number>()

  const [selectedOptionNames, setSelectedOptionNames] = useState(
    // By default only the option in the 1st position is selected
    productOptions
      ?.filter((option) => option.position === 1)
      ?.map((option) => option.name) ?? [],
  )

  // Preview variant
  const previewVariant = useMemo(() => {
    return productVariants?.find((variant) => {
      return variant.variantID === previewVariantId
    })
  }, [previewVariantId, productVariants])

  // Product option labels
  const productOptionLabels = useMemo(() => {
    return productOptions?.map((option) => {
      return getOptionListLabel(activeProduct?.optionNames ?? [], option)
    })
  }, [activeProduct, productOptions])

  // Product option items
  const productOptionsList = useMemo(() => {
    if (!activeVariant) {
      return
    }

    return productOptions?.map((option) => {
      return getProductOptions(
        option,
        activeVariant.options,
        selectedOptionNames,
        productGalleryPhotos ?? [],
        productOptionSettings ?? [],
        productVariants ?? [],
      )
    })
  }, [
    activeVariant,
    productGalleryPhotos,
    productOptions,
    productOptionSettings,
    productVariants,
    selectedOptionNames,
  ])

  // Handle selected option name and active variant changes
  const handleSelect = useCallback(
    (optionName: string, optionValue: string) => {
      setSelectedOptionNames((currentSelectedOptionNames) => {
        if (currentSelectedOptionNames?.includes(optionName)) {
          return currentSelectedOptionNames
        }

        return [...(currentSelectedOptionNames ?? []), optionName]
      })

      if (!activeVariant) {
        return
      }

      let newVariant = getVariantFromOptions(
        productVariants ?? [],
        activeVariant.options,
        optionName,
        optionValue,
      )

      if (!newVariant) {
        newVariant = getFallbackVariantFromOptions(
          productVariants ?? [],
          optionName,
          optionValue,
        )
      }

      if (newVariant && newVariant.variantID !== activeVariant?.variantID) {
        setActiveVariantId(newVariant.variantID)
      }
    },
    [activeVariant, productVariants, setActiveVariantId],
  )

  // Handle preview variant changes
  const handlePreview = useCallback(
    (optionName: string, optionValue?: string) => {
      if (!enablePreview || !activeVariant) {
        return
      }

      if (!optionValue) {
        setPreviewVariantId(undefined)
        return
      }

      const newVariant = getVariantFromOptions(
        productVariants ?? [],
        activeVariant.options,
        optionName,
        optionValue,
      )

      if (newVariant) {
        setPreviewVariantId(newVariant.variantID)
      }
    },
    [activeVariant, enablePreview, productVariants, setPreviewVariantId],
  )

  if (!activeCombinedListing || !activeProduct || !activeVariant) {
    return null
  }

  return (
    <section className={className}>
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-12">
        <ProductHeroGallery
          photosets={productGalleryPhotos ?? []}
          variant={activeVariant}
          previewVariant={previewVariant}
        />
        <ProductHeroContent
          title={activeCombinedListing.title ?? activeProduct.title}
          description={activeProduct.description}
          shoppingInfo={shoppingInfo}
          product={activeProduct}
          variant={activeVariant}
          options={productOptions ?? []}
          productPrice={activeProduct.price}
          productComparePrice={activeProduct.comparePrice}
          isEveryOptionSelected={
            typeof productOptions !== 'undefined' &&
            selectedOptionNames.length === productOptions.length
          }
          productOptionsList={productOptionsList ?? []}
          productOptionLabels={productOptionLabels ?? []}
          isPreorderActive={false}
          isAvailabilityCountdownActive={false}
          onSelect={handleSelect}
          onPreview={handlePreview}
        />
      </div>
    </section>
  )
}

export default CombinedListingProductHero
